import { siteData } from "../data/intelligence";
import type { Project, Task } from "../lib/types";

function TaskArchive({ task }: { task: Task }) {
  return <article className="task-card">
    <h5>{task.title}</h5>
    <dl className="fact-list task-facts">
      <div><dt>Task ID</dt><dd>{task.id}</dd></div>
      <div><dt>项目</dt><dd>{task.projectName}</dd></div>
      <div><dt>类型</dt><dd>{task.type}</dd></div>
      <div><dt>任务状态</dt><dd>{task.status}</dd></div>
      <div><dt>任务说明</dt><dd>{task.description}</dd></div>
      <div><dt>任务目的</dt><dd>{task.purpose}</dd></div>
      <div><dt>与资格的关系</dt><dd>{task.eligibilityRelationship}</dd></div>
      <div><dt>官方入口</dt><dd><a href={task.officialEntryUrl} target="_blank" rel="noopener noreferrer">{task.officialEntry}</a></dd></div>
      <div><dt>来源等级</dt><dd>{task.sourceLevel}</dd></div>
      <div><dt>前置条件</dt><dd>{task.prerequisites.join("；")}</dd></div>
      <div><dt>支持地区</dt><dd>{task.supportedRegions}</dd></div>
      <div><dt>KYC</dt><dd>{task.kyc}</dd></div>
      <div><dt>网络</dt><dd>{task.network}</dd></div>
      <div><dt>所需资产</dt><dd>{task.requiredAssets}</dd></div>
      <div><dt>建议钱包</dt><dd>{task.walletGuidance}</dd></div>
      <div><dt>预计 Gas</dt><dd>{task.gas}</dd></div>
      <div><dt>预计资金</dt><dd>{task.cost}</dd></div>
      <div><dt>锁定期</dt><dd>{task.lockup}</dd></div>
      <div><dt>预计时间</dt><dd>{task.estimatedTime}</dd></div>
      <div><dt>执行频率</dt><dd>{task.frequency}</dd></div>
      <div><dt>任务截止</dt><dd>{task.deadline}</dd></div>
      <div><dt>快照相关性</dt><dd>{task.snapshotRelevance}</dd></div>
      <div><dt>奖励状态</dt><dd>{task.rewardStatus}</dd></div>
      <div><dt>最后核验</dt><dd>{task.lastVerifiedAt}</dd></div>
    </dl>
    <h5>任务步骤</h5><ol>{task.steps.map((item) => <li key={item}>{item}</li>)}</ol>
    <h5>核验方式</h5><ul>{task.verification.map((item) => <li key={item}>{item}</li>)}</ul>
    <h5>需保留的证据</h5><ul>{task.evidenceToKeep.map((item) => <li key={item}>{item}</li>)}</ul>
    <h5>注意事项</h5><ul>{task.precautions.map((item) => <li key={item}>{item}</li>)}</ul>
    <h5>常见失败与安全排查顺序</h5><ol>{task.commonFailures.map((item) => <li key={item}>{item}</li>)}</ol>
    <h5>风险提示</h5><p>{task.riskWarning}</p>
    <h5>女巫与合规说明</h5><p>{task.complianceNotes}</p>
    <h5>停止条件与更安全替代</h5><ul>{task.stopConditions.map((item) => <li key={item}>{item}</li>)}</ul><p><strong>更安全替代：</strong>{task.saferAlternative}</p>
    <h5>最低成本路径</h5><p>{task.minimumCostPath}</p>
    <h5>标准参与路径</h5><p>{task.standardPath}</p>
    <h5>进阶参与路径</h5><p>{task.advancedPath}</p>
    <h5>不建议执行的任务</h5><ul>{task.notRecommended.map((item) => <li key={item}>{item}</li>)}</ul>
    <h5>停止投入条件</h5><p>{task.investmentStopConditions}</p>
  </article>;
}

export function ProjectArchive({ projects, openProjects, onToggle }: { projects: Project[]; openProjects: Set<string>; onToggle: (slug: string) => void }) {
  return <div><h2>项目档案</h2>{projects.map((project) => {
    const open = openProjects.has(project.slug);
    const evidence = siteData.evidence.filter((item) => item.projectId === project.id);
    const events = siteData.events.filter((item) => item.projectId === project.id);
    const tasks = siteData.tasks.filter((item) => item.projectId === project.id);
    return <article id={`archive-${project.slug}`} key={project.id} className="project-card archive-card"><div className="project-card-head"><div><h3>{project.name} · {project.rating} 级</h3><p>{project.hardStop ? "⛔ 停止参与" : project.conclusion}</p></div><button type="button" aria-expanded={open} aria-controls={`project-${project.slug}`} onClick={() => onToggle(project.slug)}>{open ? "收起档案" : "展开档案"}</button></div>{open && <div id={`project-${project.slug}`}>
      <p><strong>结论：</strong>{project.conclusion}</p><p><strong>证据基础：</strong>{project.evidenceBasis}</p><p><strong>未知项：</strong>{project.unknowns.join("；")}</p>
      <dl className="fact-list"><div><dt>TGE 状态</dt><dd>{project.tgeStatus}</dd></div><div><dt>快照状态</dt><dd>{project.snapshotStatus}</dd></div><div><dt>项目截止</dt><dd>{project.deadline}</dd></div></dl>
      <section><h4>事件时间</h4><ul>{events.map((event) => <li key={event.id}><strong>{event.label}</strong>：{event.dateOrWindow}；原时区 {event.timezone}；用户时区 {event.localTime}；状态 {event.status}；来源等级 {event.sourceLevel}；最后核验 {event.lastVerifiedAt}</li>)}</ul></section>
      {project.hardStop ? <div className="alert alert-danger"><strong>⛔ 停止参与</strong><span>身份链、Season 状态与积分信息存在冲突；仅保留只读风险、事件与证据记录，不提供任何资金操作、签名或交易步骤。</span></div> : <section><h4>任务档案</h4>{tasks.map((task) => <TaskArchive key={task.id} task={task} />)}</section>}
      <section><h4>公开证据</h4><ul>{evidence.map((source) => <li key={source.id}><a href={source.url} target="_blank" rel="noopener noreferrer">{source.sourceName} · {source.level} 级证据</a><span>：{source.claim}；发布/事件时间 {source.publishedOrEventAt}；证据状态 {source.status}；最后核验 {source.lastVerifiedAt}</span></li>)}</ul></section>
    </div>}</article>;
  })}</div>;
}
